import { useCallback, useEffect, useState } from 'react';
import { api, logHttpError } from '../../services/api';

type ModerationEntityType = 'posts' | 'comments' | 'messages' | 'sticker-packs';

interface ModerationItem {
  id: number;
  author_id: number;
  author_username: string;
  summary: string;
  created_at: string;
}

const ENTITY_FILTERS: Array<{ value: ModerationEntityType; label: string }> = [
  { value: 'posts', label: 'Посты' },
  { value: 'comments', label: 'Комментарии' },
  { value: 'messages', label: 'Сообщения' },
  { value: 'sticker-packs', label: 'Стикерпаки' },
];

const PAGE_SIZE = 50;

const ModerationPanel = () => {
  const [entityType, setEntityType] = useState<ModerationEntityType>('posts');
  const [items, setItems] = useState<ModerationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const userId = localStorage.getItem('sharkly_user_id');
  const currentUserId = userId ? Number(userId) : null;

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get<ModerationItem[]>(`/moderation/${entityType}`, {
        params: { limit: PAGE_SIZE, offset },
      });
      setItems(response.data);
      setHasNextPage(response.data.length === PAGE_SIZE);
    } catch (error: unknown) {
      logHttpError('Ошибка загрузки модерации', error);
      setError('Не удалось загрузить контент для модерации.');
      setItems([]);
      setHasNextPage(false);
    } finally {
      setLoading(false);
    }
  }, [entityType, offset]);

  useEffect(() => {
    void fetchItems();
  }, [fetchItems]);

  const handleDelete = async (item: ModerationItem) => {
    const confirmed = window.confirm(
      `Удалить ${ENTITY_FILTERS.find(filter => filter.value === entityType)?.label.toLowerCase()} #${item.id} пользователя @${item.author_username}?`,
    );
    if (!confirmed) return;

    try {
      setDeletingId(item.id);
      setError(null);
      await api.delete(`/moderation/${entityType}/${item.id}`);
      setItems(currentItems => currentItems.filter(current => current.id !== item.id));
    } catch (error: unknown) {
      logHttpError('Ошибка удаления при модерации', error);
      setError('Не удалось удалить контент. Возможно, он уже удалён или принадлежит вам.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <section className="space-y-5" aria-labelledby="moderation-heading">
      <div className="flex flex-col gap-4 rounded-2xl border border-gray-800 bg-[#1a1d24] p-5 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 id="moderation-heading" className="text-xl font-bold text-white">Модерация контента</h2>
          <p className="mt-1 text-sm text-gray-400">Просмотр и удаление чужого контента.</p>
        </div>
        <button
          type="button"
          onClick={() => void fetchItems()}
          disabled={loading}
          className="rounded-lg border border-gray-700 bg-gray-800 px-4 py-2 text-sm font-bold text-white transition hover:bg-gray-700 disabled:opacity-50"
        >
          🔄 Обновить
        </button>
      </div>

      <div className="flex flex-wrap gap-2" role="tablist" aria-label="Тип контента">
        {ENTITY_FILTERS.map(filter => (
          <button
            key={filter.value}
            type="button"
            role="tab"
            aria-selected={entityType === filter.value}
            onClick={() => {
              setOffset(0);
              setEntityType(filter.value);
            }}
            className={`rounded-lg border px-4 py-2 text-sm font-bold transition ${
              entityType === filter.value
                ? 'border-red-500/50 bg-red-500/20 text-red-300'
                : 'border-gray-700 bg-gray-800 text-gray-300 hover:bg-gray-700'
            }`}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300" role="alert">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-red-500 border-t-transparent" />
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-gray-800 bg-[#1a1d24] py-16 text-center text-gray-400">
          Контент этого типа не найден.
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-gray-800 bg-[#1a1d24] shadow-xl">
          <div className="divide-y divide-gray-800/70">
            {items.map(item => {
              const isOwnContent = currentUserId === item.author_id;
              return (
                <article key={item.id} className="flex flex-col gap-4 p-5 transition hover:bg-gray-800/30 md:flex-row md:items-center md:justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                      <span className="font-mono text-gray-500">#{item.id}</span>
                      <span className="font-bold text-red-300">@{item.author_username}</span>
                      <span className="text-gray-500">{new Date(item.created_at).toLocaleString('ru-RU')}</span>
                    </div>
                    <p className="mt-2 break-words text-gray-200">{item.summary}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => void handleDelete(item)}
                    disabled={deletingId === item.id || isOwnContent}
                    title={isOwnContent ? 'Администратор не может удалить собственный контент' : 'Удалить контент'}
                    className="shrink-0 rounded-lg border border-red-500/30 bg-red-500/20 px-4 py-2 text-sm font-bold text-red-300 transition hover:bg-red-500/30 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    {isOwnContent ? 'Ваш контент' : deletingId === item.id ? 'Удаление…' : '🗑️ Удалить'}
                  </button>
                </article>
              );
            })}
          </div>
        </div>
      )}

      {!loading && (
        <nav className="flex items-center justify-between gap-3" aria-label="Страницы модерации">
          <button
            type="button"
            onClick={() => setOffset(current => Math.max(0, current - PAGE_SIZE))}
            disabled={offset === 0}
            className="rounded-lg border border-gray-700 bg-gray-800 px-4 py-2 text-sm font-bold text-white transition hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-40"
          >
            ← Назад
          </button>
          <span className="text-sm text-gray-400">Страница {Math.floor(offset / PAGE_SIZE) + 1}</span>
          <button
            type="button"
            onClick={() => setOffset(current => current + PAGE_SIZE)}
            disabled={!hasNextPage}
            className="rounded-lg border border-gray-700 bg-gray-800 px-4 py-2 text-sm font-bold text-white transition hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Далее →
          </button>
        </nav>
      )}
    </section>
  );
};

export default ModerationPanel;
